import { createApiClient } from './client'

// Types
export type MilitaryRole =
  | 'trip_commander'
  | 'navigator'
  | 'quartermaster'
  | 'chef'
  | 'medic'
  | 'comms_media'
  | null

export interface MilitaryRoleInfo {
  value: MilitaryRole
  label: string
  description: string
  icon: string
  color: string
}

/** Shared pill styling per design system: adventure scale, semantic tokens. */
const ROLE_PILL_STYLE = 'bg-adventure-100 text-adventure-800 border border-adventure-200'

export const MILITARY_ROLES: MilitaryRoleInfo[] = [
  {
    value: 'trip_commander',
    label: 'Trip Commander',
    description: 'Mission leader and decision maker',
    icon: '',
    color: ROLE_PILL_STYLE,
  },
  {
    value: 'navigator',
    label: 'Navigator',
    description: 'Route planning and GPS specialist',
    icon: '',
    color: ROLE_PILL_STYLE,
  },
  {
    value: 'quartermaster',
    label: 'Quartermaster',
    description: 'Gear, supplies, and campsite setup specialist',
    icon: '',
    color: ROLE_PILL_STYLE,
  },
  {
    value: 'chef',
    label: 'Chef',
    description: 'Meal planning, cooking, and food service specialist',
    icon: '',
    color: ROLE_PILL_STYLE,
  },
  {
    value: 'medic',
    label: 'Medic',
    description: 'First aid and medical specialist',
    icon: '',
    color: ROLE_PILL_STYLE,
  },
  {
    value: 'comms_media',
    label: 'Comms & Media',
    description: 'Radio communication and trip documentation specialist',
    icon: '',
    color: ROLE_PILL_STYLE,
  },
]

export function getRoleLabel(role: MilitaryRole): string | null {
  if (!role) return null
  return MILITARY_ROLES.find((r) => r.value === role)?.label ?? null
}

export interface UpdateMilitaryRoleRequest {
  military_role: MilitaryRole
}

export class MilitaryRolesApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  // Update member military role
  async updateMemberMilitaryRole(tripId: string, memberId: string, roleData: UpdateMilitaryRoleRequest): Promise<void> {
    await this.apiClient.put(`/trips/${tripId}/members/${memberId}/military-role`, roleData)
  }

  // Get military role info by value
  getMilitaryRoleInfo(role: MilitaryRole): MilitaryRoleInfo | null {
    if (!role) return null
    return MILITARY_ROLES.find(r => r.value === role) || null
  }

  // Get all available military roles
  getAllMilitaryRoles(): MilitaryRoleInfo[] {
    return MILITARY_ROLES
  }

  // Get available roles (excluding already assigned ones)
  getAvailableRoles(assignedRoles: MilitaryRole[]): MilitaryRoleInfo[] {
    return MILITARY_ROLES.filter(role => 
      role.value === null || !assignedRoles.includes(role.value)
    )
  }
}
